import { LoadingButton } from '@mui/lab';
import {
    Autocomplete,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormGroup,
    MenuItem,
    Select,
    TextField,
} from '@mui/material';

import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { designAPI } from '@api/designs';
import { useStyles } from '../styles/DialogStyles.styles';
import { COMPATIBILITY_OPTIONS, System } from '@consts/systems';
import { Sides } from '@features/redux/slices/designSlice';

import type { RootState } from '@features/redux/store';
import type { Coverage } from '@features/redux/slices/designSlice';

const LANGUAGES = ['eng'];

interface CoverageEditDialogProps {
    coverage?: Coverage;
    cancel: (updated: boolean) => void;
}

export const CoverageEditDialog = ({ coverage, cancel }: CoverageEditDialogProps) => {
    const styles = useStyles();

    const { IdToken } = useSelector((state: RootState) => state.auth);

    const defaultActive = coverage ? coverage.active : true;

    const [active, setActive] = useState(defaultActive);

    const [loading, setLoading] = useState(false);

    const [coverageName, setCoverageName] = useState(coverage ? coverage.coverageName : '');
    const [isCase, setCase] = useState<boolean>(false);
    const [facing, setFacing] = useState<Sides>(Sides.Front);
    const [compatability, setCompatability] = useState<string[]>(COMPATIBILITY_OPTIONS);

    const [languageCode, setLanguageCode] = useState('eng');

    const { postCoverage, getCoverage } = designAPI(IdToken as string);

    const initCoverage = async (coverage: Coverage) => {
        if (coverage) {
            const { coverageId, languageCode } = coverage;

            setLoading(true);

            const data = await getCoverage(coverageId, languageCode);
            const { facing, isCase, compatability } = data;

            const compatArray = compatability ? compatability.split(',') : [];

            setFacing(facing);
            setCase(isCase ? isCase : false);
            setCompatability(compatArray);

            setLoading(false);
        }
    };

    useEffect(() => {
        if (coverage) {
            initCoverage(coverage);
        }
    }, []);

    const validateFormData = () => {
        return coverageName && compatability.length;
    };

    const saveCoverage = async () => {
        if (!validateFormData()) {
            alert('Fill in all data!');
            return;
        }

        setLoading(true);

        const method = coverage ? 'PUT' : 'POST';

        const options = compatability.length ? { isCase, facing } : {};

        const idOption = coverage ? { coverageId: coverage.coverageId } : {};

        try {
            await postCoverage(
                {
                    ...idOption,
                    ...options,
                    coverageName,
                    active,
                    languageCode,
                    compatability: compatability.join(','),
                },
                method
            );

            cancel(true);
        } catch (error) {
            setLoading(false);
            alert('Error occured while uploading data!');
        }
    };

    return (
        <Dialog open={true} fullWidth maxWidth={'md'}>
            <DialogTitle className={styles.dialogTitle}>{coverage ? 'Edit Coverage' : 'Add Coverage'}</DialogTitle>

            <DialogContent className={styles.dialogContent}>
                <TextField
                    label='Coverage Name'
                    className={styles.textField}
                    value={coverageName}
                    onChange={(e) => setCoverageName(e.target.value)}
                />

                {coverage && (
                    <div style={{ display: 'flex', flex: 1, marginTop: '20px' }}>
                        <Autocomplete
                            disablePortal
                            value={languageCode}
                            options={LANGUAGES}
                            onChange={(_, value) => setLanguageCode(value as string)}
                            renderInput={(params) => <TextField {...params} label='Language' />}
                        />
                    </div>
                )}

                <div style={{ display: 'flex', flex: 1, marginTop: '20px' }}>
                    <Autocomplete
                        disablePortal
                        multiple
                        options={COMPATIBILITY_OPTIONS}
                        value={compatability}
                        onChange={(_, value) => setCompatability(value)}
                        renderInput={(params) => <TextField {...params} label='Compatability' />}
                    />
                </div>

                {compatability.includes(System.RCMini) && (
                    <div style={{ display: 'flex', flex: 1, marginTop: '20px' }}>
                        <Select
                            value={facing}
                            onChange={(e) => setFacing(e.target.value as Sides)}
                            variant='outlined'
                            style={{ maxHeight: '38px' }}
                            label={'facing'}>
                            {Object.values(Sides).map((side) => (
                                <MenuItem value={side} key={side}>
                                    {side}
                                </MenuItem>
                            ))}
                        </Select>
                    </div>
                )}

                <FormGroup className={styles.checkboxContainer}>
                    {compatability.includes(System.RCMini) && (
                        <FormControlLabel
                            control={<Checkbox checked={isCase} onChange={(e) => setCase(e.target.checked)} />}
                            label={'Case'}
                        />
                    )}
                    <FormControlLabel
                        control={<Checkbox checked={active} onChange={(e) => setActive(e.target.checked)} />}
                        label={'active'}
                    />
                </FormGroup>
            </DialogContent>

            <DialogActions className={styles.dialogActions}>
                <LoadingButton loading={loading} className={styles.save} onClick={saveCoverage} variant={'contained'}>
                    Save
                </LoadingButton>
                <Button disabled={loading} onClick={() => cancel(false)}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
};
